import React, { useCallback, useRef, useState } from 'react';
import Alert from 'shared/components/UC/Alert';
import Button from 'shared/components/UC/Button';
import styles from './ImportDropzone.module.css';

const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.csv'];
const MAX_SIZE = 15 * 1024 * 1024;

interface ImportDropzoneProps {
  quotaPerEvaluator: number;
  minEvaluatorsPerApplication: number;
  analyzing: boolean;
  onFileSelected: (file: File) => void;
}

const hasAcceptedExtension = (name: string): boolean =>
  ACCEPTED_EXTENSIONS.some((ext) => name.toLowerCase().endsWith(ext));

const ImportDropzone: React.FC<ImportDropzoneProps> = ({
  quotaPerEvaluator,
  minEvaluatorsPerApplication,
  analyzing,
  onFileSelected,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accept = useCallback(
    (file: File | undefined) => {
      if (!file) return;
      if (!hasAcceptedExtension(file.name)) {
        setError(
          `Formato no admitido. El archivo debe ser ${ACCEPTED_EXTENSIONS.join(', ')}.`
        );
        return;
      }
      if (file.size > MAX_SIZE) {
        setError('El archivo supera los 15 MB.');
        return;
      }
      setError(null);
      onFileSelected(file);
    },
    [onFileSelected]
  );

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    if (analyzing) return;
    accept(event.dataTransfer.files?.[0]);
  };

  return (
    <div className={styles.layout}>
      <div className={styles.dropColumn}>
        <div
          className={`${styles.dropzone} ${dragActive ? styles.dragActive : ''} ${
            analyzing ? styles.busy : ''
          }`}
          onDragEnter={(e) => {
            e.preventDefault();
            if (!analyzing) setDragActive(true);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={(e) => {
            e.preventDefault();
            setDragActive(false);
          }}
          onDrop={handleDrop}
        >
          <i className="uc-icon" aria-hidden="true">
            upload_file
          </i>
          <p className="uc-p-body">
            {analyzing
              ? 'Analizando el archivo y cruzándolo con las postulaciones del concurso...'
              : 'Arrastra aquí el archivo de resultado del match'}
          </p>
          {analyzing ? (
            <div
              className={styles.progressTrack}
              role="progressbar"
              aria-label="Analizando archivo"
            >
              <div className={styles.progressFill} />
            </div>
          ) : (
            <>
              <span className={`uc-p-body-small ${styles.hint}`}>
                Formatos admitidos: XLSX o CSV, hasta 15 MB
              </span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => inputRef.current?.click()}
              >
                Seleccionar archivo
              </Button>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            className={styles.input}
            accept={ACCEPTED_EXTENSIONS.join(',')}
            onChange={(e) => {
              accept(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        <Alert variant="info">
          En el prototipo el archivo no se lee: el análisis se genera con las
          postulaciones y evaluadores reales de este concurso.
        </Alert>
      </div>

      <aside className={styles.contract} aria-label="Formato esperado del archivo">
        <h3 className="uc-h5">Qué debe traer el archivo</h3>
        <dl className={styles.columns}>
          <div>
            <dt className="uc-label">folio</dt>
            <dd className="uc-p-body-small">
              Número de la postulación, por ejemplo AV26018.
            </dd>
          </div>
          <div>
            <dt className="uc-label">email_evaluador</dt>
            <dd className="uc-p-body-small">
              Debe corresponder a un evaluador ya registrado en la plataforma.
            </dd>
          </div>
          <div>
            <dt className="uc-label">nombre_evaluador</dt>
            <dd className="uc-p-body-small">Solo para verificación visual.</dd>
          </div>
          <div>
            <dt className="uc-label">confianza</dt>
            <dd className="uc-p-body-small">
              Afinidad del match entre 0 y 1. Bajo 0,55 se marca como confianza baja.
            </dd>
          </div>
        </dl>
        <h3 className={`uc-h5 ${styles.rulesTitle}`}>Reglas del concurso</h3>
        <ul className={`uc-p-body-small ${styles.rules}`}>
          <li>Mínimo {minEvaluatorsPerApplication} evaluadores por postulación.</li>
          <li>Máximo {quotaPerEvaluator} proyectos por evaluador.</li>
          <li>Una línea por cada par postulación y evaluador.</li>
        </ul>
      </aside>
    </div>
  );
};

export default ImportDropzone;
